import { type InteractionType, type ProspectDto } from "@crowdcoursing/api/openapi";
import { Delete, Edit } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { DateTime } from "luxon";

import { InteractionForm } from "~ui/molecules/InteractionForm/InteractionForm";
import { getInteractionTypeInfo } from "~utils/constants/interactionTypes";

const tWith = "with";
const tEditInteraction = "Edit interaction";
const tDeleteInteraction = "Delete interaction";
const tCancel = "Cancel";
const tSaveChanges = "Save Changes";
const tEditInteractionTitle = "Edit Interaction";

export interface InteractionCardProps {
  interaction: NonNullable<ProspectDto["interactions"]>[number];
  prospect: ProspectDto;
  interactionModal: {
    editInteraction: NonNullable<ProspectDto["interactions"]>[number] | null;
    openEditModal: (interaction: NonNullable<ProspectDto["interactions"]>[number]) => void;
    closeEditModal: () => void;
    handleUpdateInteraction: (values: {
      type: InteractionType;
      subject: string;
      notes: string;
      contactId: string | null;
      occurredAt: string;
    }) => Promise<void>;
    handleDeleteInteraction: (interactionId: string) => Promise<void>;
  };
}

export const InteractionCard = ({ interaction, prospect, interactionModal }: InteractionCardProps) => {
  const contact = interaction.contactId
    ? prospect.contacts?.find((contact) => contact.id === interaction.contactId)
    : null;

  return (
    <>
      <Paper
        variant="outlined"
        sx={{
          alignItems: "start",
          display: "grid",
          gap: 1,
          gridTemplateColumns: "1fr auto",
          p: 2,
        }}
      >
        <Box display="grid" gap={1}>
          {/* Header */}
          <Box display="flex" alignItems="center" gap={1}>
            <Chip
              icon={getInteractionTypeInfo(interaction.type).icon}
              label={getInteractionTypeInfo(interaction.type).label}
              sx={{
                "& .MuiChip-icon": {
                  color: getInteractionTypeInfo(interaction.type).color,
                },
              }}
              size="small"
              variant="outlined"
            />
            {contact && (
              <Typography variant="body2" color="text.secondary">
                {tWith} {[contact.firstName, contact.lastName].filter(Boolean).join(" ")}
              </Typography>
            )}
          </Box>

          {/* Subject */}
          {interaction.subject && <Typography variant="subtitle1">{interaction.subject}</Typography>}

          {/* Notes */}
          {interaction.notes && (
            <Typography variant="body2" color="text.secondary">
              {interaction.notes}
            </Typography>
          )}

          {/* Timestamp */}
          <Typography variant="caption" color="text.secondary">
            {DateTime.fromISO(interaction.occurredAt ?? "").toRelative()}
          </Typography>
        </Box>

        {/* Actions */}
        <Stack direction="row" spacing={1}>
          <Tooltip title={tEditInteraction}>
            <IconButton size="small" onClick={() => interactionModal.openEditModal(interaction)}>
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={tDeleteInteraction}>
            <IconButton
              size="small"
              color="error"
              onClick={() => interactionModal.handleDeleteInteraction(interaction.id ?? "")}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Paper>

      {/* Edit Dialog */}
      <Dialog
        open={interactionModal.editInteraction?.id === interaction.id}
        onClose={interactionModal.closeEditModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{tEditInteractionTitle}</DialogTitle>
        <DialogContent>
          <Box pt={1}>
            <InteractionForm
              contacts={prospect.contacts ?? undefined}
              initialValues={{
                contactId: interaction.contactId,
                notes: interaction.notes ?? "",
                occurredAt: interaction.occurredAt ?? "",
                subject: interaction.subject ?? "",
                type: interaction.type ?? 99,
              }}
              onSubmit={interactionModal.handleUpdateInteraction}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={interactionModal.closeEditModal}>{tCancel}</Button>
          <Button type="submit" form="interaction-form" variant="contained">
            {tSaveChanges}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
